/**
 * ClienteLayout — header simples + Outlet pro Portal Cliente (B1.5).
 *
 * Distinto do AdminLayout (que tem sidebar IGM + sub-nav + 5 grupos).
 * Cliente externo precisa de UX simplificada — só logo + nome user + logout.
 *
 * Placeholder pra:
 *   - Seletor multi-cliente (ativado em B1.6 quando ClienteContext entrar)
 *   - Sino de notificações (B1.6+)
 *
 * Layout responsivo: mobile-first (cliente Portaria provavelmente mobile no
 * dia-a-dia). Header sticky-top.
 *
 * NÃO REVERTER:
 *   - Logo IGM Security horizontal /logo-email.png (mesma identidade do email)
 *   - Tema warm paper (light) / warm dark (dark) consistente com V2
 *   - Sem links externos pro V2 admin (cliente NUNCA vê admin)
 *   - LogoutButton chama useClienteAuth().logout (não AuthContext interno)
 */

import { useState } from 'react';
import { Link, Outlet, useNavigate } from 'react-router-dom';

import { Button } from '@/components/ui';
// LAYOUT-CANONICO: as constantes vêm do arquivo do PageContainer (e não do
// barrel) de propósito — só CASCA usa a régua crua; tela usa <PageContainer>.
import {
  PageContainer,
  REGUA_LARGURA,
  REGUA_PADDING_X,
} from '@/components/ui/PageContainer';
import { ClienteSeletor } from '@/components/cliente/ClienteSeletor';
import { ClienteNav } from '@/components/cliente/ClienteNav';
// F3.2: sino de notificações (gated pela flag via dashboard — OFF não renderiza).
import { NotificationBell } from '@/components/cliente/NotificationBell';
import { useClienteAuth } from '@/contexts/ClienteAuthContext';
import { ImpersonationBanner } from '@/components/ImpersonationBanner';
import { cn } from '@/lib/cn';

export function ClienteLayout() {
  const { user, isAuthenticated, isLoading, logout, impersonatedBy } = useClienteAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [saindoModoTeste, setSaindoModoTeste] = useState(false);

  async function handleLogout() {
    await logout();
    setMenuOpen(false);
    navigate('/cliente/login');
  }

  // F3: sair do modo teste → logout do portal + volta pro admin.
  async function sairModoTeste() {
    setSaindoModoTeste(true);
    await logout();
    window.location.assign('/admin');
  }

  return (
    <div className="min-h-screen bg-paper dark:bg-paper-dark text-ink-1 dark:text-ink-dark-1">
      {impersonatedBy && (
        <ImpersonationBanner alvoNome={user?.nome ?? 'cliente'} onSair={sairModoTeste} saindo={saindoModoTeste} />
      )}
      <header
        className="sticky top-0 z-10 bg-paper-surface dark:bg-paper-dark-surface border-b border-line dark:border-line-dark"
        data-testid="cliente-header"
      >
        {/* LAYOUT-CANONICO: o `max-w-6xl px-4` próprio saiu — cabeçalho, nav,
            conteúdo e rodapé do portal usam a MESMA régua do app interno. */}
        <div
          className={cn(
            REGUA_LARGURA,
            REGUA_PADDING_X,
            'flex items-center justify-between gap-4 py-3',
          )}
        >
          {/* Logo + seletor de cliente (B1.6 multi-cliente) */}
          <div className="flex items-center gap-3 min-w-0">
            <Link
              to={isAuthenticated ? '/cliente/dashboard' : '/cliente/login'}
              className="flex items-center flex-shrink-0"
              data-testid="cliente-logo-link"
            >
              <img
                src="/logo-email.png"
                alt="IGM Security"
                className="h-9 md:h-10 w-auto"
              />
            </Link>
            {isAuthenticated && (
              <div className="hidden md:block border-l border-line dark:border-line-dark pl-3">
                <ClienteSeletor />
              </div>
            )}
          </div>

          {/* Right side: user OR login link */}
          {isLoading ? (
            <span className="text-sm text-ink-2 dark:text-ink-dark-2">Carregando…</span>
          ) : isAuthenticated && user ? (
            <div className="flex items-center gap-1">
              <NotificationBell />
              <div className="relative">
              <button
                type="button"
                onClick={() => setMenuOpen((o) => !o)}
                className="flex max-w-[42vw] items-center gap-2 rounded-lg px-3 py-2 text-sm transition hover:bg-paper-warm dark:hover:bg-paper-dark-warm sm:max-w-none"
                aria-expanded={menuOpen}
                aria-haspopup="menu"
                data-testid="cliente-user-menu-toggle"
              >
                <span className="truncate font-medium">{user.nome}</span>
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="none"
                  className={`transition ${menuOpen ? 'rotate-180' : ''}`}
                >
                  <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" />
                </svg>
              </button>
              {menuOpen && (
                <div
                  className="absolute right-0 z-20 mt-1 w-56 rounded-xl border border-line bg-paper-surface py-1 shadow-card-hover dark:border-line-dark dark:bg-paper-dark-elevated"
                  role="menu"
                  data-testid="cliente-user-menu"
                >
                  <div className="border-b border-line px-3 py-2 dark:border-line-dark">
                    <div className="truncate text-sm font-medium">{user.nome}</div>
                    <div className="truncate text-xs text-ink-2 dark:text-ink-dark-2">
                      {user.email}
                    </div>
                  </div>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={handleLogout}
                    className="w-full px-3 py-2.5 text-left text-sm transition hover:bg-paper-warm dark:hover:bg-paper-dark-warm"
                    data-testid="cliente-logout-btn"
                  >
                    Sair
                  </button>
                </div>
              )}
              </div>
            </div>
          ) : (
            <Link to="/cliente/login">
              <Button variant="ghost" size="sm">
                Entrar
              </Button>
            </Link>
          )}
        </div>
      </header>

      {/* Seletor mobile (abaixo do header em telas pequenas) */}
      {isAuthenticated && (
        <div className="md:hidden border-b border-line dark:border-line-dark bg-paper-surface dark:bg-paper-dark-surface px-4 py-2">
          <ClienteSeletor />
        </div>
      )}

      {/* B1.7: sub-nav horizontal com links condicionais por permissão */}
      <ClienteNav />

      {/* LAYOUT-CANONICO: a margem/largura do conteúdo do portal passa a vir do
          PageContainer — a mesma régua das telas do app interno. Por isso as
          telas de `src/pages/cliente/` não têm (nem podem ter) wrapper próprio. */}
      <main>
        <PageContainer>
          <Outlet />
        </PageContainer>
      </main>

      {/* O endereço do rodapé apontava pro ambiente de BETA (`beta.` no host) —
          endereço interno na cara do cliente. Passa a apontar pro site oficial. */}
      <footer
        className={cn(
          REGUA_LARGURA,
          REGUA_PADDING_X,
          'mt-12 border-t border-line py-6 text-center text-xs text-ink-2 dark:border-line-dark dark:text-ink-dark-2',
        )}
      >
        Portal do Cliente · Grupo IGM Security · Compromisso com a sua segurança ·{' '}
        <a
          href="https://www.igmsecurity.com.br"
          target="_blank"
          rel="noopener noreferrer"
          className="underline hover:text-red-deep dark:hover:text-red-glow"
        >
          igmsecurity.com.br
        </a>
      </footer>
    </div>
  );
}
